import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
} from '@mui/material';
import { BackButton, TableAccordionInfinite, TableAccordionColumn } from '@marageti/z4-lib';
import { View } from '@marageti/z4-sdk/lib/tracking';
import AuditFilters from '../audit-filters';
import { formatDateSafe } from '../audit-utils';

interface ViewsViewFilters {
  entity: string;
  day: string;
  device: string;
}

interface ViewsViewProps {
  views: View[];
  isLoading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
  filters: ViewsViewFilters;
  onFilterChange: (name: string, value: unknown) => void;
}

const columns: TableAccordionColumn<View>[] = [
  {
    fieldPath: 'entityType',
    label: 'Entity Type',
  },
  {
    fieldPath: 'entityId',
    label: 'Entity ID',
  },
  {
    fieldPath: 'email',
    label: 'Email',
  },
  {
    fieldPath: 'country',
    label: 'Country',
  },
  {
    fieldPath: 'osFamily',
    label: 'OS',
  },
  {
    fieldPath: 'recaptchaScore',
    label: 'Recaptcha Score',
  },
  {
    fieldPath: 'createdAt',
    label: 'Created At',
    style: { width: '202px', maxWidth: '202px', minWidth: '202px' },
    format: (value) => formatDateSafe(value),
  },
  {
    fieldPath: 'deviceType',
    label: 'Device',
    isInAccordion: true,
  },
  {
    fieldPath: 'clientName',
    label: 'Client',
    isInAccordion: true,
  },
  {
    fieldPath: 'id',
    label: 'ID',
    isInAccordion: true,
  },
  {
    fieldPath: 'updateId',
    label: 'Update ID',
    isInAccordion: true,
  },
  {
    fieldPath: 'deviceId',
    label: 'Device ID',
    isInAccordion: true,
  },
  {
    fieldPath: 'userId',
    label: 'User ID',
    isInAccordion: true,
  },
  {
    fieldPath: 'canonicalPath',
    label: 'Canonical Path',
    isInAccordion: true,
  },
  {
    fieldPath: 'path',
    label: 'Path',
    isInAccordion: true,
  },
  {
    fieldPath: 'title',
    label: 'Title',
    isInAccordion: true,
  },
  {
    fieldPath: 'userAgent',
    label: 'User Agent',
    isInAccordion: true,
  },
  {
    fieldPath: 'clientType',
    label: 'Client Type',
    isInAccordion: true,
  },
  {
    fieldPath: 'ipAddress',
    label: 'IP Address',
    isInAccordion: true,
  },
  {
    fieldPath: 'referrer',
    label: 'Referrer',
    isInAccordion: true,
  },
  {
    fieldPath: 'referrerDomain',
    label: 'Referrer Domain',
    isInAccordion: true,
  },
  {
    fieldPath: 'width',
    label: 'Width',
    isInAccordion: true,
  },
  {
    fieldPath: 'height',
    label: 'Height',
    isInAccordion: true,
  },
  {
    fieldPath: 'updatedAt',
    label: 'Updated At',
    isInAccordion: true,
    format: (value) => formatDateSafe(value),
  },
  {
    fieldPath: 'expiresAt',
    label: 'Expires At',
    isInAccordion: true,
    format: (value) => formatDateSafe(value),
  },
  {
    fieldPath: 'googleClientID',
    label: 'Google Client ID',
    isInAccordion: true,
  },
];

const PageViewsView: React.FC<ViewsViewProps> = ({
  views,
  isLoading,
  error,
  hasMore,
  loadMore,
  filters,
  onFilterChange,
}) => {
  const navigate = useNavigate();

  const filterFields = [
    {
      name: 'entity',
      label: 'Entity',
      type: 'text' as const,
      value: filters.entity || '',
      onChange: (value: unknown) => onFilterChange('entity', value),
    },
    {
      name: 'day',
      label: 'Day',
      type: 'date' as const,
      value: filters.day || '',
      onChange: (value: unknown) => onFilterChange('day', value),
    },
    {
      name: 'device',
      label: 'Device',
      type: 'text' as const,
      value: filters.device || '',
      onChange: (value: unknown) => onFilterChange('device', value),
    },
  ];

  return (
    <Box>
      <Stack alignItems="center" justifyContent="flex-start" flexDirection="row" gap={1} sx={{ mb: 2 }}>
        <BackButton onClick={() => navigate('/settings/audit')} />
        <Typography variant="h5">
          Page Views
        </Typography>
      </Stack>

      <AuditFilters
        fields={filterFields}
        error={error}
        total={`${views.length} views loaded`}
      />

      <TableAccordionInfinite<View>
        columns={columns}
        data={views}
        getRowId={(view) => view.id}
        isLoading={isLoading}
        hasMore={hasMore}
        loadMore={loadMore}
        emptyMessage="No views found. Try adjusting your filters."
        isEmpty={views.length === 0}
      />
    </Box>
  );
};

export default PageViewsView;
